import React, { useCallback, useMemo } from 'react'

import {
  clearSearchAndFetch,
  selectIsUnifiedSearchEnabled,
  selectSearchPage,
  setFilters,
} from '@disco/state-management/reducers/search-page'
import { hasFilterValue } from '@disco/utils/search-page'

import type { FilterState } from 'types/filter-page'

import {
  FilterActionAddButton,
  FilterActionClearAllButton,
  SelectedFilterBadges,
} from 'components/search/search-filters'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { useFiltersDefinition } from '../utils'

const isFilterValueActive = (value: unknown): boolean => {
  if (value === undefined || value === null) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  return true
}

const SearchFilters = (): React.ReactNode | null => {
  const dispatch = useDispatch()
  const handleFiltersChange = useCallback(
    (value: FilterState[]) => dispatch(setFilters(value)),
    [dispatch],
  )
  const handleClearAll = useCallback(
    (clearedFilters: FilterState[]) => {
      void dispatch(clearSearchAndFetch(clearedFilters))
    },
    [dispatch],
  )
  const filtersDefinition = useFiltersDefinition()
  const { filters, query } = useSelector(selectSearchPage)

  const unifiedSearchEnabled = useSelector(selectIsUnifiedSearchEnabled)

  const { hasActiveFilterValues, hasLegacyFilterValues, hasOperator } = useMemo(
    () =>
      filters.reduce(
        (acc, f) => ({
          hasActiveFilterValues:
            acc.hasActiveFilterValues || isFilterValueActive(f.value),
          // Looser than isFilterValueActive on purpose: legacy Reset also
          // treats an empty array as clearable.
          hasLegacyFilterValues:
            acc.hasLegacyFilterValues || hasFilterValue(f.value),
          hasOperator: acc.hasOperator || !!f.operator,
        }),
        {
          hasActiveFilterValues: false,
          hasLegacyFilterValues: false,
          hasOperator: false,
        },
      ),
    [filters],
  )
  const hasClearableState = hasActiveFilterValues || hasOperator || !!query
  const showClearAll = unifiedSearchEnabled ? hasClearableState : true
  const isClearAllDisabled =
    !unifiedSearchEnabled && !hasLegacyFilterValues && !query

  return (
    <>
      <FilterActionAddButton
        value={filters}
        definitions={filtersDefinition}
        onChange={handleFiltersChange}
      />
      <SelectedFilterBadges
        value={filters}
        definitions={filtersDefinition}
        seedDefaultState={!unifiedSearchEnabled}
        onChange={handleFiltersChange}
      />
      {showClearAll ? (
        <FilterActionClearAllButton
          definitions={filtersDefinition}
          disabled={isClearAllDisabled}
          seedDefaultState={!unifiedSearchEnabled}
          onChange={handleClearAll}
        />
      ) : null}
    </>
  )
}

export default SearchFilters
